import { useEffect, useState } from "react";
import { GripVertical, Plus, X } from "lucide-react";

import { searchArtists } from "../../Services/SocialService";
import ArtistAvatar from "../ArtistAvatar";
import type { ArtistHit, FavoriteArtist } from "../../data/types";

type Props = {
  artists: FavoriteArtist[];
  onChange: (artists: FavoriteArtist[]) => void;
};

/**
 * Listans ordning är rankningen — översta artisten är favorit nummer ett.
 * Backend skriver om hela listan vid spar, så ordningen här är sanningen.
 *
 * Inget tak på antalet. Appen är till för musikälskare, och en lång lista är
 * ett rimligt uttryck för det.
 *
 * Bara artister som finns i Beatspots katalog går att välja. Favoriten lagras
 * som en relation till ett internt artist-ID, så fritext finns inte längre.
 */
export default function FavoriteArtistsEditor({ artists, onChange }: Props) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<ArtistHit[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const trimmed = q.trim();

    if (trimmed.length < 2) {
      setHits([]);
      return;
    }

    let alive = true;
    setIsSearching(true);

    const timer = window.setTimeout(async () => {
      const result = await searchArtists(trimmed).catch(() => [] as ArtistHit[]);

      if (!alive) return;

      setHits(result);
      setIsSearching(false);
    }, 250);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [q]);

  const addArtist = (hit: ArtistHit) => {
    if (artists.some((a) => a.id === hit.id)) return;

    onChange([...artists, { id: hit.id, name: hit.name, rank: artists.length }]);

    setQ("");
    setHits([]);
  };

  const removeArtist = (id: string) => {
    onChange(artists.filter((a) => a.id !== id));
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= artists.length) return;

    const next = [...artists];
    [next[index], next[target]] = [next[target], next[index]];

    onChange(next);
  };

  return (
    <div className="artist-editor">
      <div className="artist-editor-search">
        <input
          className="profile-input"
          placeholder="Search an artist to add..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        {q.trim().length >= 2 && (
          <div className="artist-editor-hits">
            {isSearching && hits.length === 0 && (
              <p className="artist-editor-status">Searching…</p>
            )}

            {!isSearching && hits.length === 0 && (
              <p className="artist-editor-status">
                No artists found in the Beatspot catalogue.
              </p>
            )}

            {hits.map((hit) => {
              const alreadyAdded = artists.some((a) => a.id === hit.id);

              return (
                <button
                  key={hit.id}
                  type="button"
                  className="artist-editor-hit"
                  disabled={alreadyAdded}
                  onClick={() => addArtist(hit)}
                >
                  <ArtistAvatar name={hit.name} seed={hit.id} size={28} />

                  <span className="artist-editor-hit-name">{hit.name}</span>

                  {alreadyAdded ? (
                    <em>Added</em>
                  ) : (
                    <Plus size={15} strokeWidth={2.2} />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {artists.length === 0 ? (
        <p className="artist-editor-status">
          No favourite artists yet — search above to add as many as you like.
        </p>
      ) : (
        <ol className="artist-editor-list">
          {artists.map((artist, index) => (
            <li key={artist.id} className="artist-editor-row">
              <div className="artist-editor-rank">
                <button
                  type="button"
                  aria-label={`Move ${artist.name} up`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  ▲
                </button>
                <span>{index + 1}</span>
                <button
                  type="button"
                  aria-label={`Move ${artist.name} down`}
                  disabled={index === artists.length - 1}
                  onClick={() => move(index, 1)}
                >
                  ▼
                </button>
              </div>

              <GripVertical size={14} className="artist-editor-grip" />

              <ArtistAvatar name={artist.name} seed={artist.id} size={38} />

              <span className="artist-editor-name">{artist.name}</span>

              <button
                type="button"
                className="artist-editor-remove"
                aria-label={`Remove ${artist.name}`}
                onClick={() => removeArtist(artist.id)}
              >
                <X size={15} strokeWidth={2.2} />
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
